import { useEffect, useState } from 'react';
import { Edit, Trash2, Plus } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

import {
    ResponsiveTable, TableHeader, TableRow,
    TableCell, StatusBadge, Modal,
    Input, SelectInput, Textarea
} from './ProductTableComponents';

import ComboItemsBuilder from './ComboItemsBuilder';
import { PRODUCT_TYPE } from '../../constants/commonConstants.js';
import Pagination from '../common/Pagination';
import CloudinaryImageUpload from './CloudinaryImageUpload';

const DEFAULT_FORM = {
    name: '',
    description: '',
    product_id: '',
    unit_quantity: '',
    mrp: '',
    strikethrough_mrp: '',
    gst_percentage: '0',
    hsn_code: '',
    image_urls: [],
    combo_items: [],
};

export default function ProductVariants({
    variants = [],
    totalPages,
    pageSize,
    totalRecords,
    currentPage,
    handlePageChange,
    onRefresh,
    isLoading = false,
}) {
    const BASE_URL = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(DEFAULT_FORM);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState(DEFAULT_FORM);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [productOptions, setProductOptions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const set = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const setEdit = (field, value) =>
        setEditForm((prev) => ({ ...prev, [field]: value }));

    const isComboProduct = (productId) =>
        productOptions.find((p) => String(p.value) === String(productId))?.product_type === PRODUCT_TYPE.COMBO;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        if (isUploadingImage) {
            toast.error('Wait for the image upload to finish');
            return;
        }

        const is_combo = isComboProduct(form.product_id);

        if (
            (!is_combo && Number(form.unit_quantity) < 0) ||
            Number(form.mrp) < 0 ||
            (form.strikethrough_mrp !== '' && Number(form.strikethrough_mrp) < 0) ||
            Number(form.gst_percentage) < 0
        ) {
            toast.error('Unit quantity, prices, and GST percentage cannot be negative');
            return;
        }

        if (is_combo && form.combo_items.length === 0) {
            toast.error('Add at least one item to the combo');
            return;
        }

        if (
            form.strikethrough_mrp !== '' &&
            (!Number.isFinite(Number(form.strikethrough_mrp)) || Number(form.strikethrough_mrp) <= Number(form.mrp))
        ) {
            toast.error('Strikethrough MRP must be greater than MRP');
            return;
        }

        try {
            setIsSubmitting(true);

            await axios({
                method: 'POST',
                url: BASE_URL + '/product-management/product-variants',
                headers: { Authorization: token },
                data: {
                    ...form,
                    is_combo,
                    unit_quantity: is_combo ? 1 : Number(form.unit_quantity),
                    mrp: Number(form.mrp),
                    strikethrough_mrp: form.strikethrough_mrp === '' ? null : Number(form.strikethrough_mrp),
                    gst_percentage: Number(form.gst_percentage),
                    combo_items: is_combo
                        ? form.combo_items.map(({ component_variant_id, quantity }) => ({
                            component_variant_id,
                            quantity,
                        }))
                        : undefined,
                },
            });

            toast.success(`Variant "${form.name}" created`);

            setShowModal(false);
            setForm(DEFAULT_FORM);
            onRefresh();
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (variant) => {
        setSelectedId(variant.id);

        setEditForm({
            name: variant.name || '',
            description: variant.description || '',
            product_id: variant.product_id || '',
            unit_quantity: variant.unit_quantity || '',
            mrp: variant.mrp || '',
            strikethrough_mrp: variant.strikethrough_mrp ?? '',
            gst_percentage: variant.gst_percentage ?? 0,
            hsn_code: variant.hsn_code || '',
            image_urls: variant.image_urls || [],
            combo_items: (variant.combo_items || []).map((item) => ({
                component_variant_id: item.component_variant_id,
                quantity: item.quantity,
                label: `${item.component_variant?.name || ''} (${item.component_variant?.product?.name || ''})`,
            })),
        });

        setShowEditModal(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        if (isUploadingImage) {
            toast.error('Wait for the image upload to finish');
            return;
        }

        const is_combo = isComboProduct(editForm.product_id);

        if (
            (!is_combo && Number(editForm.unit_quantity) < 0) ||
            Number(editForm.mrp) < 0 ||
            (editForm.strikethrough_mrp !== '' && Number(editForm.strikethrough_mrp) < 0) ||
            Number(editForm.gst_percentage) < 0
        ) {
            toast.error('Unit quantity, prices, and GST percentage cannot be negative');
            return;
        }

        if (is_combo && editForm.combo_items.length === 0) {
            toast.error('Add at least one item to the combo');
            return;
        }

        if (
            editForm.strikethrough_mrp !== '' &&
            (!Number.isFinite(Number(editForm.strikethrough_mrp)) || Number(editForm.strikethrough_mrp) <= Number(editForm.mrp))
        ) {
            toast.error('Strikethrough MRP must be greater than MRP');
            return;
        }

        try {
            setIsSubmitting(true);

            await axios({
                method: 'PUT',
                url: `${BASE_URL}/product-management/product-variants/${selectedId}`,
                headers: { Authorization: token },
                data: {
                    ...editForm,
                    unit_quantity: is_combo ? 1 : Number(editForm.unit_quantity),
                    mrp: Number(editForm.mrp),
                    strikethrough_mrp: editForm.strikethrough_mrp === '' ? null : Number(editForm.strikethrough_mrp),
                    gst_percentage: Number(editForm.gst_percentage),
                    combo_items: is_combo
                        ? editForm.combo_items.map(({ component_variant_id, quantity }) => ({
                            component_variant_id,
                            quantity,
                        }))
                        : undefined,
                },
            });

            toast.success('Variant updated');

            setShowEditModal(false);
            setEditForm(DEFAULT_FORM);
            setSelectedId(null);

            onRefresh();
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const openDeleteModal = (id) => {
        setSelectedId(id);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);

            await axios({
                method: 'DELETE',
                url: `${BASE_URL}/product-management/product-variants/${selectedId}`,
                headers: { Authorization: token },
            });

            toast.success('Variant deleted');

            setShowDeleteModal(false);
            setSelectedId(null);

            onRefresh();
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const fetchProductOptions = async () => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${BASE_URL}/product-management/products/options`,
                headers: { Authorization: token },
            });

            const options = (response?.data?.data || []).map((p) => {
                const details = p.product_type === PRODUCT_TYPE.COMBO ? 'Combo' : p.base_unit;
                const category = p.category?.name;

                return {
                    label: category ? `${p.name} (${category}, ${details})` : `${p.name} (${details})`,
                    value: p.id,
                    product_type: p.product_type,
                };
            });

            setProductOptions(options);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchProductOptions();
    }, []);

    return (
        <>
            {/* Header */}
            <div className="mb-4 flex justify-end">
                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                    <Plus className="w-5 h-5" />
                    Add Variant
                </button>
            </div>

            {/* Table */}
            <ResponsiveTable>
                <thead>
                    <tr>
                        <TableHeader>ID</TableHeader>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Product</TableHeader>
                        <TableHeader>Qty</TableHeader>
                        <TableHeader>MRP</TableHeader>
                        <TableHeader>GST</TableHeader>
                        <TableHeader>Images</TableHeader>
                        <TableHeader>Status</TableHeader>
                        <TableHeader>Actions</TableHeader>
                    </tr>
                </thead>

                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={9} className="py-5 text-center">
                                <div className="flex flex-col items-center justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                                    <p className="text-sm text-gray-600">Loading...</p>
                                </div>
                            </td>
                        </tr>
                    ) : variants.length === 0 ? (
                        <tr>
                            <td colSpan={9} className="py-5 text-center text-gray-500">
                                No Variants found
                            </td>
                        </tr>
                    ) : null}
                    {variants.map((variant, index) => (
                        <TableRow key={variant.id}>
                            <TableCell>
                                {(currentPage - 1) * pageSize + index + 1}
                            </TableCell>

                            <TableCell>
                                <div className="font-medium">{variant.name}</div>
                                <div className="w-[180px] max-w-[70vw] whitespace-normal break-words text-xs text-gray-500 sm:w-[220px] lg:w-[280px]">
                                    {variant.description}
                                </div>
                            </TableCell>

                            <TableCell>
                                {variant.product?.name || '—'}
                            </TableCell>

                            <TableCell>
                                {variant.product?.product_type === PRODUCT_TYPE.COMBO ? (
                                    <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                                        Combo ({variant.combo_items?.length || 0} items)
                                    </span>
                                ) : (
                                    `${variant.unit_quantity} ${variant.product?.base_unit || ''}`
                                )}
                            </TableCell>

                            <TableCell>
                                {variant.strikethrough_mrp && (
                                    <div className="text-xs text-gray-500 line-through">₹{variant.strikethrough_mrp}</div>
                                )}
                                ₹{variant.mrp}
                            </TableCell>

                            <TableCell>{variant.gst_percentage ?? 0}%</TableCell>

                            <TableCell>
                                <div className="flex gap-1">
                                    {variant.image_urls?.slice(0, 2).map((img, i) => (
                                        <img key={i} src={img} className="w-8 h-8 rounded" />
                                    ))}
                                </div>
                            </TableCell>

                            <TableCell>
                                <StatusBadge active={variant.is_active} />
                            </TableCell>

                            <TableCell>
                                <div className="flex gap-2">
                                    <button onClick={() => handleEdit(variant)}>
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => openDeleteModal(variant.id)}>
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                    </button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </tbody>
            </ResponsiveTable>

            {/* CREATE MODAL */}
            {showModal && (
                <Modal
                    title="Add Product Variant"
                    size="lg"
                    disabled={isSubmitting}
                    onClose={() => {
                        setShowModal(false);
                        setForm(DEFAULT_FORM);
                    }}
                >
                    <form onSubmit={handleSubmit} className="space-y-4">

                        <Input
                            label="Variant Name *"
                            value={form.name}
                            onChange={(e) => set('name', e.target.value)}
                            required
                        />

                        <Textarea
                            label="Description"
                            value={form.description}
                            onChange={(e) => set('description', e.target.value)}
                        />

                        <SelectInput
                            label="Product *"
                            value={form.product_id}
                            onChange={(e) => set('product_id', e.target.value)}
                            options={productOptions}
                            placeholder="Select Product"
                            required
                        />

                        {isComboProduct(form.product_id) ? (
                            <ComboItemsBuilder
                                items={form.combo_items}
                                onChange={(items) => set('combo_items', items)}
                                productOptions={productOptions}
                            />
                        ) : (
                            <Input
                                label="Unit Quantity *"
                                type="number"
                                min="0"
                                step="any"
                                value={form.unit_quantity}
                                onChange={(e) => set('unit_quantity', e.target.value)}
                                required
                            />
                        )}

                        <Input
                            label="MRP *"
                            type="number"
                            min="0"
                            step="any"
                            value={form.mrp}
                            onChange={(e) => set('mrp', e.target.value)}
                            required
                        />

                        <Input
                            label="Strikethrough MRP"
                            type="number"
                            min="0"
                            step="any"
                            value={form.strikethrough_mrp}
                            onChange={(e) => set('strikethrough_mrp', e.target.value)}
                        />

                        {!isComboProduct(form.product_id) && (
                            <>
                                <Input
                                    label="GST Percentage"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="any"
                                    value={form.gst_percentage}
                                    onChange={(e) => set('gst_percentage', e.target.value)}
                                />

                                <Input
                                    label="HSN Code"
                                    value={form.hsn_code}
                                    onChange={(e) => set('hsn_code', e.target.value)}
                                />
                            </>
                        )}

                        <CloudinaryImageUpload
                            value={form.image_urls}
                            onChange={(imageUrls) => set('image_urls', imageUrls)}
                            multiple
                            baseUrl={BASE_URL}
                            token={token}
                            onUploadingChange={setIsUploadingImage}
                        />

                        <div className="flex gap-2 pt-2">
                            <button
                                type="submit"
                                disabled={isSubmitting || isUploadingImage}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                            >
                                {isSubmitting ? 'Creating...' : 'Create Variant'}
                            </button>

                            <button
                                type="button"
                                disabled={isSubmitting}
                                onClick={() => {
                                    setShowModal(false);
                                    setForm(DEFAULT_FORM);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                        </div>

                    </form>
                </Modal>
            )}
            
            {/* EDIT MODAL */}
            {showEditModal && (
                <Modal
                    title="Edit Product Variant"
                    size="lg"
                    disabled={isSubmitting}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditForm(DEFAULT_FORM);
                        setSelectedId(null);
                    }}
                >
                    <form onSubmit={handleUpdate} className="space-y-4">

                        <Input
                            label="Variant Name *"
                            value={editForm.name}
                            onChange={(e) => setEdit('name', e.target.value)}
                            required
                        />

                        <Textarea
                            label="Description"
                            value={editForm.description}
                            onChange={(e) => setEdit('description', e.target.value)}
                        />

                        <SelectInput
                            label="Product *"
                            value={editForm.product_id}
                            onChange={(e) => setEdit('product_id', e.target.value)}
                            options={productOptions}
                            placeholder="Select Product"
                            required
                        />

                        {isComboProduct(editForm.product_id) ? (
                            <ComboItemsBuilder
                                items={editForm.combo_items}
                                onChange={(items) => setEdit('combo_items', items)}
                                productOptions={productOptions}
                            />
                        ) : (
                            <Input
                                label="Unit Quantity *"
                                type="number"
                                min="0"
                                step="any"
                                value={editForm.unit_quantity}
                                onChange={(e) => setEdit('unit_quantity', e.target.value)}
                                required
                            />
                        )}

                        <Input
                            label="MRP *"
                            type="number"
                            min="0"
                            step="any"
                            value={editForm.mrp}
                            onChange={(e) => setEdit('mrp', e.target.value)}
                            required
                        />

                        <Input
                            label="Strikethrough MRP"
                            type="number"
                            min="0"
                            step="any"
                            value={editForm.strikethrough_mrp}
                            onChange={(e) => setEdit('strikethrough_mrp', e.target.value)}
                        />

                        {!isComboProduct(editForm.product_id) && (
                            <>
                                <Input
                                    label="GST Percentage"
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="any"
                                    value={editForm.gst_percentage}
                                    onChange={(e) => setEdit('gst_percentage', e.target.value)}
                                />

                                <Input
                                    label="HSN Code"
                                    value={editForm.hsn_code}
                                    onChange={(e) => setEdit('hsn_code', e.target.value)}
                                />
                            </>
                        )}

                        <CloudinaryImageUpload
                            value={editForm.image_urls}
                            onChange={(imageUrls) => setEdit('image_urls', imageUrls)}
                            multiple
                            baseUrl={BASE_URL}
                            token={token}
                            onUploadingChange={setIsUploadingImage}
                        />

                        <div className="flex gap-2 pt-2">
                            <button
                                type="submit"
                                disabled={isSubmitting || isUploadingImage}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
                            >
                                {isSubmitting ? 'Updating...' : 'Update Variant'}
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setShowEditModal(false);
                                    setEditForm(DEFAULT_FORM);
                                    setSelectedId(null);
                                }}
                                className="px-4 py-2 border rounded-lg"
                            >
                                Cancel
                            </button>
                        </div>

                    </form>
                </Modal>
            )}

            {/* DELETE MODAL */}
            {showDeleteModal && (
                <Modal title="Delete Variant" onClose={() => setShowDeleteModal(false)}>
                    <div className="space-y-4">
                        <p className="text-gray-600">
                            This action cannot be undone. Are you sure you want to delete this category?
                        </p>
                        <div className="flex flex-row gap-2 pt-2">
                            <button
                                onClick={handleDelete}
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                                {isSubmitting ? 'Deleting...' : 'Delete'}
                            </button>

                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedId(null);
                                }}
                                className="flex-1 px-4 py-2 border rounded-lg"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {totalPages > 0 && (
                <div className="px-4 py-1 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Showing <span className="font-medium">{pageSize}</span> rows per page
                        <span className="mx-2">•</span>
                        <span className="font-medium">{totalRecords}</span> total results
                    </div>

                    {totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    )}
                    </div>
                </div>
            )}
        </>
    );
}
